import React from 'react';
import { Link } from 'react-router-dom';
import { Typography } from '@mui/material';
import './TradingPhilosophy.css';
import Seo from '../common/Seo';
import { Footer } from '../layout';

const TradingPhilosophy = () => {
  return (
    <div className="ntb-philosophy-container">
      <Seo
        title="Trading Philosophy - NanoTradingCopilot"
        description="Discipline over prediction. Process over emotion. Discover the core principles behind NanoTradingCopilot's validation engine."
        keywords="trading philosophy, trading psychology, disciplined trading, risk management rules"
      />
      {/* Header */}
      <header className="ntb-philosophy-header">
        <Typography variant="h1" className="ntb-philosophy-title">
          Trading Philosophy
        </Typography>
        <Typography variant="body1" className="ntb-philosophy-subtitle">
          Discipline over prediction. Process over emotion.
        </Typography>
      </header>

      {/* Core Belief */}
      <section className="ntb-philosophy-section">
        <blockquote className="ntb-philosophy-quote">
          "I trade to build sustainable wealth through knowledge-driven decisions, not emotion-based reactions."
        </blockquote>
      </section>

      {/* Principles */}
      <section className="ntb-philosophy-section">
        <Typography variant="h2" className="ntb-section-title">
          Core Principles
        </Typography>
        <div className="ntb-principles-grid">
          <div className="ntb-principle">
            <Typography variant="h3" className="ntb-principle-title">Rules First</Typography>
            <Typography variant="body2" className="ntb-principle-desc">
              Define your trading rules before you trade. Risk per trade, minimum R:R,
              entry criteria—write them down and follow them.
            </Typography>
          </div>
          <div className="ntb-principle">
            <Typography variant="h3" className="ntb-principle-title">Validate Always</Typography>
            <Typography variant="body2" className="ntb-principle-desc">
              Every trade should pass your validation criteria. If it doesn't meet
              your rules, don't take it. No exceptions.
            </Typography>
          </div>
          <div className="ntb-principle">
            <Typography variant="h3" className="ntb-principle-title">Capital Preservation</Typography>
            <Typography variant="body2" className="ntb-principle-desc">
              Protect your capital first. You can't trade without it.
              Small losses are acceptable; large losses are not.
            </Typography>
          </div>
          <div className="ntb-principle">
            <Typography variant="h3" className="ntb-principle-title">Continuous Improvement</Typography>
            <Typography variant="body2" className="ntb-principle-desc">
              Review your trades. Learn from losses. Adjust your rules
              based on evidence, not emotion.
            </Typography>
          </div>
        </div>
      </section>

      {/* The Problem & Solution */}
      <section className="ntb-philosophy-section ntb-philosophy-problem">
        <div className="ntb-philosophy-columns">
          <div className="ntb-philosophy-column">
            <Typography variant="h2" className="ntb-section-title">The Problem</Typography>
            <Typography variant="body1" className="ntb-philosophy-text">
              Traders know their rules. They've read the books, taken the courses,
              defined their edge. But when the market moves, emotion takes over.
            </Typography>
            <ul className="ntb-philosophy-list">
              <li>Skipping checklists when excited</li>
              <li>Ignoring stop losses when hoping</li>
              <li>Taking trades that don't meet criteria</li>
              <li>Overtrading after wins or losses</li>
            </ul>
          </div>
          <div className="ntb-philosophy-column">
            <Typography variant="h2" className="ntb-section-title">The Solution</Typography>
            <Typography variant="body1" className="ntb-philosophy-text">
              Automate your discipline. NanoTradingCopilot validates every trade
              against your rules before you execute. Your rules, enforced automatically.
            </Typography>
            <ul className="ntb-philosophy-list">
              <li>Define your validation rules</li>
              <li>Input your trade idea</li>
              <li>Get validation result</li>
              <li>Trade with confidence</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Simple Rules */}
      <section className="ntb-philosophy-section">
        <Typography variant="h2" className="ntb-section-title">
          Simple Rules, Consistent Results
        </Typography>
        <Typography variant="body2" className="ntb-rules-subtitle">
          Examples of commonly enforced rules (fully configurable by the trader)
        </Typography>
        <div className="ntb-rules-examples">
          <div className="ntb-rule-example">
            <span className="ntb-rule-label">Risk</span>
            <span className="ntb-rule-value">Never risk more than 1-2% per trade</span>
          </div>
          <div className="ntb-rule-example">
            <span className="ntb-rule-label">R:R</span>
            <span className="ntb-rule-value">Minimum 2:1 reward-to-risk ratio</span>
          </div>
          <div className="ntb-rule-example">
            <span className="ntb-rule-label">Trend</span>
            <span className="ntb-rule-value">Trade with the trend, not against it</span>
          </div>
          <div className="ntb-rule-example">
            <span className="ntb-rule-label">Timing</span>
            <span className="ntb-rule-value">Avoid low-liquidity windows</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ntb-philosophy-cta">
        <Typography variant="h2" className="ntb-cta-title">
          Enforce Your Discipline
        </Typography>
        <Typography variant="body1" className="ntb-cta-text">
          Start validating your trades against your own rules.
        </Typography>
        <Link to="/signup" className="ntb-btn ntb-btn-primary">
          Start validating your trades
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default TradingPhilosophy;
